import { createPlayerInput } from '../entities/player.js';
import { configureAudio, getAudioSettings } from '../audio/audio.js';
import { cycleMusicTrack, getMusicState, toggleMusicEnabled } from '../audio/music.js';

let input = null;
let gamepadState = {
  connected: false,
  buttons: new Set(),
};

export function initInput(handlers = {}) {
  input = createPlayerInput();

  window.addEventListener('keydown', (event) => {
    input.keys.add(event.code);
    if (event.code === 'Tab') {
      event.preventDefault();
      if (event.repeat) return;
      handlers.onWeaponWheelShow?.(event);
      return;
    }
    if (event.repeat) return;
    if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') handlers.onDash?.(event);
    if (event.code === 'Space') handlers.onFire?.(event);
    if (event.code === 'Enter') handlers.onStart?.(event);
    if (event.code === 'Escape' || event.code === 'KeyP') handlers.onTogglePause?.(event);
    if (event.code === 'KeyM') toggleMute();
    if (event.code === 'KeyN') cycleMusicTrack();
    if (event.code === 'KeyO') handlers.onPlatformOverlay?.(event);
    if (event.code.startsWith('Digit')) {
      const index = Number(event.code.slice(5)) - 1;
      if (index >= 0) handlers.onSelectWeapon?.(index, event);
    }
  });

  window.addEventListener('keyup', (event) => {
    input.keys.delete(event.code);
    if (event.code === 'Tab') {
      event.preventDefault();
      handlers.onWeaponWheelHide?.(event);
    }
  });

  window.addEventListener('pointerdown', (event) => {
    if (event.button === 0) { input.shooting = true; handlers.onFire?.(event); }
    if (event.button === 2) handlers.onADS?.(true, event);
  });

  window.addEventListener('pointerup', (event) => {
    if (event.button === 0) input.shooting = false;
    if (event.button === 2) handlers.onADS?.(false, event);
  });

  window.addEventListener('contextmenu', (event) => {
    event.preventDefault();
  });

  window.addEventListener('wheel', (event) => {
    event.preventDefault();
    handlers.onCameraZoom?.(event.deltaY < 0 ? 1 : -1, event);
  }, { passive: false });

  window.addEventListener('blur', () => {
    input.keys.clear();
    input.shooting = false;
  });

  return input;
}

function toggleMute() {
  const settings = getAudioSettings();
  configureAudio({ muted: !settings.muted });
  window.dispatchEvent(new CustomEvent('dead-loop:audio-change', { detail: getMusicState() }));
  if (settings.muted && getAudioSettings().musicEnabled) toggleMusicEnabled(true);
}

export function getInput() {
  if (!input) input = createPlayerInput();
  return input;
}

export function pollGamepad(handlers = {}) {
  if (!input) input = createPlayerInput();
  const pad = Array.from(navigator.getGamepads?.() || []).find(Boolean);
  if (!pad) {
    setGamepadConnected(false);
    clearGamepadKeys();
    input.aimVector = null;
    return false;
  }

  setGamepadConnected(true);
  const lx = axis(pad.axes[0]);
  const ly = axis(pad.axes[1]);
  const rx = axis(pad.axes[2]);
  const ry = axis(pad.axes[3]);
  setGamepadKey('KeyA', lx < -0.25);
  setGamepadKey('KeyD', lx > 0.25);
  setGamepadKey('KeyW', ly < -0.25);
  setGamepadKey('KeyS', ly > 0.25);
  input.aimVector = Math.hypot(rx, ry) > 0.28 ? { x: rx, z: ry } : null;

  if (pressed(pad, 7)) handlers.onFire?.();
  if (pressedEdge(pad, 6)) handlers.onDash?.();
  if (pressedEdge(pad, 5)) handlers.onCycleWeapon?.(1);
  if (pressedEdge(pad, 4)) handlers.onCycleWeapon?.(-1);
  if (pressedEdge(pad, 0)) handlers.onStart?.();
  return true;
}

export function rumbleGamepad(duration = 80, weakMagnitude = 0.3, strongMagnitude = 0.6) {
  const pad = Array.from(navigator.getGamepads?.() || []).find(Boolean);
  pad?.vibrationActuator?.playEffect?.('dual-rumble', { duration, weakMagnitude, strongMagnitude });
}

function axis(value = 0) {
  return Math.abs(value) < 0.18 ? 0 : value;
}

function pressed(pad, index) {
  return !!pad.buttons[index]?.pressed;
}

function pressedEdge(pad, index) {
  const id = String(index);
  const isDown = pressed(pad, index);
  const wasDown = gamepadState.buttons.has(id);
  if (isDown) gamepadState.buttons.add(id);
  else gamepadState.buttons.delete(id);
  return isDown && !wasDown;
}

function setGamepadKey(code, active) {
  input.gamepadKeys ||= new Set();
  if (active) {
    input.gamepadKeys.add(code);
    input.keys.add(code);
  } else if (input.gamepadKeys.has(code)) {
    input.gamepadKeys.delete(code);
    input.keys.delete(code);
  }
}

function clearGamepadKeys() {
  if (!input?.gamepadKeys) return;
  for (const key of input.gamepadKeys) input.keys.delete(key);
  input.gamepadKeys.clear();
}

function setGamepadConnected(connected) {
  if (gamepadState.connected === connected) return;
  gamepadState.connected = connected;
  document.body.classList.toggle('gamepad-active', connected);
}
